import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogTrigger,
  DialogFooterForm,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { ColumnDef } from '@tanstack/react-table';
import { Cog } from 'lucide-react';

import {
  GetUserNotificationOperationsDTO,
  useGetUserNotificationOperationsQuery,
} from './api/use-get-user-notification-operations.query';
import { useStoreUserNotificationOperationConfigMutation } from './api/use-store-user-notification-operation-config.mutation';

type UserNotificationConfigOperationModalProps = {
  message_id: string;
  userId: string;
};

type UserNotificationConfigOperationFormData = {
  operations: Record<string, boolean>;
};

export const UserNotificationConfigOperationModal = ({
  message_id,
  userId,
}: UserNotificationConfigOperationModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);

  const { data, isLoading, isFetching } = useGetUserNotificationOperationsQuery(
    {
      userId,
      messageId: message_id,
      enabled: open,
    },
    {
      onSuccess: data => {
        form.setValue(
          'operations',
          data.reduce(
            (acc, op) => {
              acc[op.id] = !!op.is_active;
              return acc;
            },
            {} as Record<string, boolean>,
          ),
        );
      },
    },
  );

  const operations = data || [];
  const storeUserNotificationOperationConfigMutation = useStoreUserNotificationOperationConfigMutation();

  const form = useForm<UserNotificationConfigOperationFormData>({
    defaultValues: {
      operations: {},
    },
  });

  // Initialize form with operations when data changes
  useEffect(() => {
    if (operations.length > 0) {
      const currentOperations = form.getValues().operations;

      // Merge new operations with existing selections
      const updatedOperations = operations.reduce(
        (acc: Record<string, boolean>, op: GetUserNotificationOperationsDTO) => {
          // Preserve existing selection or default to false
          acc[op.id] = currentOperations[op.id] !== undefined ? currentOperations[op.id] : false;
          return acc;
        },
        { ...currentOperations },
      );

      form.setValue('operations', updatedOperations);
    }
  }, [operations, form]);

  const columns: ColumnDef<GetUserNotificationOperationsDTO>[] = [
    {
      header: t('operation'),
      accessorKey: 'name',
    },
    {
      header: t('active'),
      accessorKey: 'enabled',
      cell: ({ row }) => {
        const operationId = row.original.id;
        const value = form.watch(`operations.${operationId}`) || false;

        return (
          <Switch
            checked={value}
            onCheckedChange={checked => {
              form.setValue(`operations.${operationId}`, checked, { shouldDirty: true });
            }}
          />
        );
      },
    },
  ];

  const handleSubmit = async (values: UserNotificationConfigOperationFormData) => {
    // Convert map to array of enabled operation IDs
    const enabledOperations = Object.entries(values.operations)
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    await storeUserNotificationOperationConfigMutation.mutateAsync(
      {
        user_id: userId,
        status: enabledOperations,
        message_id,
      },
      {
        onSuccess: () => {
          toast({
            title: t('success'),
            description: `✅ ${t('operation-notification-config-updated')}`,
          });
        },
      },
    );
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" title={t('config-change-status-operation-notification')}>
          <Cog size={14} />
        </Button>
      </DialogTrigger>
      <DialogContent className="h-full overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('config-change-status-operation-notification')}</DialogTitle>
        </DialogHeader>
        <Form form={form} onSubmit={form.handleSubmit(handleSubmit)} className="h-full overflow-hidden">
          <DataTable
            columns={columns}
            data={operations}
            isLoading={isLoading}
            isFetching={isFetching}
            withPagination={false}
          />
        </Form>
        <DialogFooterForm
          className="mt-4"
          onConfirm={form.handleSubmit(handleSubmit)}
          onCancel={() => setOpen(false)}
          isLoading={storeUserNotificationOperationConfigMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};
